import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Button,
} from '@mui/material';

import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import Chart from 'react-apexcharts';

import Client from './../Client';
import BackofficeReportsApi from './backofficeReportsApi';

function formatNumber(value, metric) {
  const num = Number(value || 0);

  if (metric === 'share') return num.toFixed(1) + '%';

  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';

  return num.toFixed(0);
}

class ClientsActivityComparison extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client('none'),
      data: [],
      period: {},
      metric: 'orders',
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.periodMode !== this.props.periodMode) {
      this.loadData();
    }
  }

  loadData = () => {
    const queryData = {
      periodMode: this.props.periodMode || 'last_30d',
    };

    const data = BackofficeReportsApi.getClientsRegisteredAnonymousComparison(this.state.client, queryData);

    if (data) {
      this.setState({
        data: data.rows || [],
        period: data.period || {},
      });
    } else {
      this.setState({
        data: [],
        period: {},
      });
    }
  };

  handleMetricChange = (event, newMetric) => {
    if (newMetric !== null) {
      this.setState({ metric: newMetric });
    }
  };

  csvValue = (value) => {
    if (value === null || typeof value === 'undefined') return '""';

    const cleanValue = String(value).replace(/"/g, '""');

    return `"${cleanValue}"`;
  };

  handleExportCsv = () => {
    const rows = this.state.data || [];

    if (!rows.length) {
      return;
    }

    const periodMode = this.props.periodMode || 'last_30d';
    const periodLabel = this.state.period?.label || periodMode;

    const totalOrders = rows.reduce(
      (sum, row) => sum + Number(row.orders || 0),
      0
    );

    const totalSales = rows.reduce(
      (sum, row) => sum + Number(row.value || 0),
      0
    );

    const weightedAov = totalOrders > 0
      ? totalSales / totalOrders
      : 0;

    const registeredRow = rows.find((row) =>
      String(row.label || '').toLowerCase().includes('registered')
    );

    const anonymousRow = rows.find((row) =>
      String(row.label || '').toLowerCase().includes('anonymous')
    );

    const registeredOrders = Number(registeredRow?.orders || 0);
    const registeredSales = Number(registeredRow?.value || 0);
    const anonymousOrders = Number(anonymousRow?.orders || 0);
    const anonymousSales = Number(anonymousRow?.value || 0);

    const registeredOrderShare = totalOrders > 0
      ? (registeredOrders / totalOrders) * 100
      : 0;

    const registeredSalesShare = totalSales > 0
      ? (registeredSales / totalSales) * 100
      : 0;

    const headers = [
      { key: 'label', label: 'Client type' },
      { key: 'orders', label: 'Orders' },
      { key: 'value', label: 'Sales' },
      { key: 'aov', label: 'AOV' },
      { key: 'order_share', label: 'Order share %' },
    ];

    const csvRows = [
      ['Registered vs anonymous'].map((value) => this.csvValue(value)).join(','),
      ['Period', periodLabel].map((value) => this.csvValue(value)).join(','),
      ['Period mode', periodMode].map((value) => this.csvValue(value)).join(','),
      ['Generated at', new Date().toLocaleString()].map((value) => this.csvValue(value)).join(','),
      '',
      ['Summary'].map((value) => this.csvValue(value)).join(','),
      ['Total orders', totalOrders].map((value) => this.csvValue(value)).join(','),
      ['Total sales', totalSales.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Weighted AOV', weightedAov.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Registered orders', registeredOrders].map((value) => this.csvValue(value)).join(','),
      ['Registered sales', registeredSales.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Registered order share %', `${registeredOrderShare.toFixed(1)}%`].map((value) => this.csvValue(value)).join(','),
      ['Registered sales share %', `${registeredSalesShare.toFixed(1)}%`].map((value) => this.csvValue(value)).join(','),
      ['Anonymous orders', anonymousOrders].map((value) => this.csvValue(value)).join(','),
      ['Anonymous sales', anonymousSales.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      '',
      headers.map((header) => this.csvValue(header.label)).join(','),
      ...rows.map((row) =>
        headers
          .map((header) => {
            if (header.key === 'order_share') {
              return this.csvValue(`${Number(row[header.key] || 0).toFixed(1)}%`);
            }

            if (header.key === 'value' || header.key === 'aov') {
              return this.csvValue(Number(row[header.key] || 0).toFixed(2));
            }

            return this.csvValue(row[header.key]);
          })
          .join(',')
      ),
    ];

    const csvContent = csvRows.join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', `registered_vs_anonymous_${periodMode}.csv`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  render() {
    const rows = this.state.data || [];
    const metric = this.state.metric;
    const periodLabel = this.state.period?.label || 'Last 30d';

    const categories = rows.map((row) => row.label || '');

    const values = rows.map((row) => {
      if (metric === 'value') return Number(row.value || 0);
      if (metric === 'aov') return Number(row.aov || 0);
      if (metric === 'share') return Number(row.order_share || 0);
      return Number(row.orders || 0);
    });

    const metricLabel =
      metric === 'orders'
        ? 'Orders'
        : metric === 'value'
          ? 'Sales'
          : metric === 'aov'
            ? 'AOV'
            : 'Order share';

    const options = {
      chart: {
        type: 'bar',
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 8,
          columnWidth: '48%',
          distributed: true,
        },
      },
      colors: ['#1a237e', '#607d8b'],
      grid: {
        borderColor: 'rgba(15, 23, 42, 0.08)',
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            fontSize: '11px',
            fontWeight: 700,
          },
        },
      },
      yaxis: {
        labels: {
          formatter: function (value) {
            return formatNumber(value, metric);
          },
          style: {
            fontSize: '11px',
          },
        },
      },
      tooltip: {
        y: {
          formatter: function (value) {
            return formatNumber(value, metric);
          },
        },
      },
      legend: {
        show: false,
      },
    };

    const series = [
      {
        name: metricLabel,
        data: values,
      },
    ];

    return (
      <Card
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: { xs: '14px', sm: '16px' },
          boxShadow: 'none',
          border: 'none',
          overflow: 'hidden',
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            boxSizing: 'border-box',
            p: { xs: 1.5, sm: 2 },
            '&:last-child': { pb: { xs: 1.5, sm: 2 } },
          }}
        >
          <Stack spacing={1.25} sx={{ height: '100%', minHeight: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ flexShrink: 0 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: '#111827',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.2,
                  }}
                >
                  Registered vs anonymous
                </Typography>

                <Typography
                  color="text.secondary"
                  sx={{
                    mt: 0.25,
                    fontSize: { xs: '0.76rem', sm: '0.82rem' },
                  }}
                >
                  Compare orders, sales, AOV and share · {periodLabel}
                </Typography>
              </Box>

              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadRoundedIcon />}
                disabled={!rows.length}
                onClick={this.handleExportCsv}
                sx={{
                  flexShrink: 0,
                  height: 32,
                  minWidth: 64,
                  px: 1,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: { xs: '0.68rem', sm: '0.75rem' },
                  whiteSpace: 'nowrap',
                  color: '#111827',
                  borderColor: 'rgba(15, 23, 42, 0.14)',
                  bgcolor: '#ffffff',
                  '&:hover': {
                    bgcolor: '#f8fafc',
                    borderColor: 'rgba(15, 23, 42, 0.24)',
                  },
                  '& .MuiButton-startIcon': {
                    mr: 0.5,
                    ml: 0,
                  },
                }}
              >
                CSV
              </Button>
            </Stack>

            <ToggleButtonGroup
              size="small"
              exclusive
              value={metric}
              onChange={this.handleMetricChange}
              sx={{
                flexShrink: 0,
                '& .MuiToggleButton-root': {
                  px: { xs: 0.85, sm: 1.05 },
                  py: 0.35,
                  fontSize: { xs: '0.68rem', sm: '0.74rem' },
                  fontWeight: 700,
                  textTransform: 'none',
                },
              }}
            >
              <ToggleButton value="orders">Orders</ToggleButton>
              <ToggleButton value="value">Sales</ToggleButton>
              <ToggleButton value="aov">AOV</ToggleButton>
              <ToggleButton value="share">Share</ToggleButton>
            </ToggleButtonGroup>

            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
              <Chart
                options={options}
                series={series}
                type="bar"
                height="100%"
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  }
}

export default ClientsActivityComparison;
